import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LocationStatus, SiteId, TaskId, WritableLocationStatus } from '@interfaces/IScheduler';
import { Slider } from '@components/ui/Slider/Slider';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { useSchedulerActions, useSchedulerStale, useSchedulerTask } from '../../hooks/useSchedulerStore';
import { useTaskProgressPersist } from '../../hooks/useTaskProgressPersist';
import { useTaskLocationsPersist } from '../../hooks/useTaskLocationsPersist';
import { useCurrentActor } from '@hooks/useCurrentActor';
import { useSiteLocations } from '@hooks/useSiteLocations';
import {
  AREA_TONE,
  PROGRESS_STATE_LABEL_KEY,
  applyAreaReport,
  floorReportTarget,
  meterSegments,
  openProgressSession,
  progressState,
  resetToAreaProgress,
  type IProgressSession,
} from '@d-orca/shared/client/activities';
import { LOCATION_STATUS_LABEL_KEY, nextAreaAction, type AreaAction } from '../../lib/locationStatusLabels';
import { roundedWorkHours } from '../../lib/workHours';
import { FloorZoneGrid } from '../FloorZoneGrid/FloorZoneGrid';
import { useZoneSweep } from '../FloorZoneGrid/useZoneSweep';
import { StatusMark } from './StatusMark';
import { useSchedulerCapabilities } from '../../context/SchedulerCapabilities';
import { useAreaReview } from '../AreaReview/useAreaReview';
import styles from './TaskProgressSection.module.css';

// ---------------------------------------------------------------------------
// TaskProgressSection — how a COMMITTED leaf task is going: the reported
// percentage and the area board behind it. It sits inside the commit block,
// above the promise rows: once a task is promised, progress is the live
// question and the promise is reference. An uncommitted task never renders it
// — there is nothing to report against a promise nobody made.
//
// The slider always shows the task's STORED `progressPercent` — the real value,
// including on re-open; never a re-derived area figure. Each drawer session
// starts from a SNAPSHOT of that stored % (the "base"): area changes move the
// number by an equal share of the remaining percentage per area
// (`blendProgress` — the areas not yet complete at open split `100 − base`).
// Dragging the slider takes MANUAL control for the session: it writes the
// value and freezes the area-recompute (2 of 10 done reads 20 %, slide to
// 30 % and completing areas no longer moves it). Both lift when the drawer
// re-opens: the freeze evaporates and the stored % (say 30) becomes the new
// base, so each of the 8 remaining areas is now worth (100 − 30) / 8 = 8.75 %.
// "Use area progress" resets in place to the plain area % and re-anchors.
//
// Area statuses are edited MODE-FREE on the board (one flush track of zone
// cells per floor). What a tap does is `nextAreaAction` (area review stage
// spec, ruling 3):
//   · A manager (`tasks.execute`): TAP reports an area DONE, takes a done one
//     back, and opens the REVIEW on one a Limited user submitted (approve =
//     done; reject = an issue, then out of review). Everything else (in
//     progress, delayed, overdue) is the IoT's reading of tag work time.
//   · A Limited user (`tasks.contribute`): TAP submits an area FOR REVIEW, or
//     takes the submission back — one area at a time; a done area is plain.
//   A cell's hover preview dashes in the colour of what its tap does.
//   · DRAG across cells (manager) → spreads the anchor's tap over the range,
//     with a live "wet paint" preview, committed as ONE blended progress write.
//   · PRESS a floor handle (manager) → reports the whole floor done (or takes
//     a done floor back).
// The strip above the board is a passive METER with live counts: one bar,
// one segment per status the field is actually in, each as wide as its share
// of the areas. It never wraps and never lists a zero. Area ASSIGNMENT (add /
// remove) stays in the Locations tab, linked from the board header.
//
// Progress drives status (100 % → completed, else in-progress), except that an
// unfinished checklist blocks the completed promotion — the same gate as the
// Complete button. Progress + status are immediate-write (a per-task PUT);
// area STATUS is a write-through that also moves the % server-side
// (`useTaskLocationsPersist`). All mutating
// paths early-return while the schedule is STALE (behind upstream) — the drawer
// must not dispatch or PUT a locally computed % onto a base that's about to be
// rebased.
//
// Reads the LIVE task from the store (not the panel draft) so area edits and
// the % reflect instantly.
// ---------------------------------------------------------------------------

/** The board's look, over the shared floor/zone grid. */
/** What a tap does → the cell class its hover preview dashes by. */
const TAP_CLASS: Record<AreaAction, string | undefined> = {
  complete: styles.tapsComplete,
  planned: styles.tapsPlanned,
  readyForReview: styles.tapsReadyForReview,
  review: styles.tapsReview,
};

const BOARD_CLASSES = {
  root: styles.board,
  row: styles.floorRow,
  floorBtn: styles.floorBtn,
  floorName: styles.floorName,
  floorCount: styles.floorCount,
  track: styles.track,
};

interface ITaskProgressSectionProps {
  taskId: TaskId;
  siteId: SiteId;
  /** Loaded schedule number — enables immediate persistence of the % (a per-task
   *  PUT). Absent → the store still updates optimistically (Router-free tests). */
  scheduleNumber?: string;
}

/** A COMMITTED task's progress — only a promise reports it (a milestone is
 *  never promised, so it has none: it is hit or not hit, `TaskStep`). */
export function TaskProgressSection({ taskId, siteId, scheduleNumber }: ITaskProgressSectionProps) {
  const { t } = useTranslation();
  const actor = useCurrentActor();
  // The slider, the sweep and the floor handles are `tasks.execute`; what a tap
  // on an area does is the user's `areaTier` (a Limited user submits for review).
  const { canExecute, areaTier } = useSchedulerCapabilities();
  // Narrow task selector — the section must not re-render on unrelated
  // mutations elsewhere in the site (Immer re-references the site root on any
  // edit; only this task's identity matters here).
  const task = useSchedulerTask(siteId, taskId);
  // The board reports STATUS only. The area SET is fixed from the commit on
  // (2026-09-23 amendment), and this board only shows on a committed task, so
  // it offers no way to change which areas there are.
  const stale = useSchedulerStale(siteId);
  const catalog = useSiteLocations(siteId);
  const { taskLocationStatusChanged, taskProgressSet } = useSchedulerActions();
  const persist = useTaskProgressPersist(siteId, scheduleNumber, taskId);
  // Area persistence: one request for a tap, a spread or a floor — the server
  // derives the % they move. Store updates stay optimistic (dispatched below).
  const { report: persistAreaReport } = useTaskLocationsPersist(siteId, scheduleNumber, taskId);
  // Stable ref (the `?? []` would otherwise mint a fresh array each render and
  // thrash the handler useCallback deps below).
  const locations = useMemo(() => task?.locations ?? [], [task?.locations]);
  const total = locations.length;

  // Drawer-session anchor: the stored % at open is the BASE, and the areas not
  // yet complete at open split the remaining percentage equally
  // (`blendProgress`). `manual` is the "slider has taken over" flag — while
  // set, area edits no longer recompute the % (it sticks until "Use area
  // progress"). Session-only by construction: re-initialised when the section
  // mounts (drawer re-open) or the panel switches task, via React's
  // render-time state adjustment. It never changes what the slider shows —
  // that is always the stored %.
  const [session, setSession] = useState<(IProgressSession & { taskId: TaskId }) | null>(null);
  if (task && session?.taskId !== taskId) {
    setSession({ taskId, ...openProgressSession(task) });
  }
  const isManual = session?.taskId === taskId && session.manual;

  // Progress travels ALONE since the task-commit design: reaching 100 % is not
  // "done" (done is a human decision, invariant 6) and 0 % is not "not
  // started" — it is "nothing reported yet". Log meta lets the store mirror
  // the server's `progress` entry optimistically.
  const showProgress = useCallback(
    (value: number) =>
      taskProgressSet({
        siteId, taskId, progressPercent: value,
        activityId: `act-${crypto.randomUUID()}`, at: new Date().toISOString(), by: actor.id,
      }),
    [siteId, taskId, taskProgressSet, actor.id],
  );
  const writeProgress = useCallback(
    (value: number) => {
      showProgress(value); // optimistic store
      persist(value);      // immediate PUT (no Save button)
    },
    [showProgress, persist],
  );

  const handleSliderChange = useCallback(
    (value: number) => {
      if (stale) return; // frozen while behind upstream — no session flip, store write, or PUT
      // Slider = manual for the rest of this session.
      setSession((prev) => (prev && prev.taskId === taskId ? { ...prev, manual: true } : prev));
      writeProgress(value);
    },
    [stale, taskId, writeProgress],
  );

  // Stamp `status` onto every listed zone that doesn't already have it — a
  // tap's cycle step, a spread stroke and a whole-floor press all land here.
  // Area edits move the % by the session's per-area share ONLY while
  // area-driven (one blended write for the whole batch); once progress is
  // manual, painting records the statuses but leaves the number fixed. Only
  // done areas move the %: a review submission, its undo and a reject write
  // no progress (`applyAreaReport` — area review stage spec, ruling 8).
  const applyStatuses = useCallback(
    (zoneIds: string[], status: WritableLocationStatus) => {
      if (stale || !session) return; // frozen while behind upstream — no dispatch, no PUT
      // Repainting the same status (or a zone off the task) is a no-op.
      const { targets, pct } = applyAreaReport(session, locations, zoneIds, status);
      // Each area flip mirrors the server's structured `location` log entry
      // optimistically (its own meta — distinct targets never coalesce together).
      for (const id of targets) {
        taskLocationStatusChanged({
          siteId, taskId, locationId: id, status,
          activityId: `act-${crypto.randomUUID()}`, at: new Date().toISOString(), by: actor.id,
        });
      }
      if (targets.length === 0) return;
      // Persist the areas as ONE report; the server writes them through to
      // Trusstor, then derives and stores the % they moved (QA R-04) — so the
      // % here is shown, never PUT beside them.
      persistAreaReport(targets, status, { holdProgress: session.manual });
      if (pct !== null) showProgress(pct);
    },
    [stale, session, locations, siteId, taskId, taskLocationStatusChanged, showProgress, persistAreaReport, actor.id],
  );

  // "Use area progress" — reset in place to the plain area % and re-anchor the
  // session there, so from here on each area is worth 100 / total again.
  const handleReset = useCallback(() => {
    if (stale) return; // frozen while behind upstream — no PUT
    const reset = resetToAreaProgress(locations);
    if (reset === null) return;
    setSession({ taskId, ...reset });
    writeProgress(reset.base);
  }, [stale, locations, taskId, writeProgress]);

  // The board: the floors this task touches, each carrying its ASSIGNED zones
  // and what they read (only floors with an assigned zone show).
  const statusByZone = new Map<string, LocationStatus>(locations.map((location) => [location.locationId, location.status]));
  const boardFloors = catalog.floors
    .map((floor) => ({
      id: floor.id,
      name: floor.name,
      zones: floor.zones.flatMap((zone) => {
        const status = statusByZone.get(zone.id);
        return status === undefined ? [] : [{ ...zone, status }];
      }),
    }))
    .filter((floor) => floor.zones.length > 0);
  // Trusstor's tag work time per area, as the hours the cell prints — only the
  // areas the IoT has actually seen work in (the feed omits the rest).
  const workHoursByZone = new Map<string, number>(
    locations.flatMap((location) => {
      const hours = location.workTimeMs === undefined ? null : roundedWorkHours(location.workTimeMs);
      return hours === null ? [] : [[location.locationId, hours] as const];
    }),
  );

  // ── Drag-to-spread ──────────────────────────────────────────────────────
  // A press CAPTURES what the anchor's tap would write — done from a cell that
  // is not done yet, not-done from one that is — and spreads it across the
  // contiguous range from the anchor to the hovered cell (board order, floor to
  // floor): fill-handle semantics over a two-value vocabulary, previewed live
  // and committed as ONE batch on release. A plain tap is not a sweep: it is
  // left to the trailing click, which reports that one cell.
  const sweep = useZoneSweep<WritableLocationStatus>(boardFloors, applyStatuses);

  // A manager's tap on an area awaiting review opens its review: approve
  // reports it done, reject (after its issue) takes it out of review.
  const review = useAreaReview(siteId, () => task, (_taskId, zoneId, status) => applyStatuses([zoneId], status));

  /** What a tap on `zoneId` does: open its review, or write `action`. */
  const tap = (zoneId: string, action: AreaAction) =>
    action === 'review' ? review.open(taskId, zoneId) : applyStatuses([zoneId], action);

  /** What a tap does, as the cell's a11y label says it. */
  const tapPhrase = (status: LocationStatus, action: AreaAction): string => {
    const key: Record<AreaAction, string> = {
      review: 'tapToReview',
      complete: 'tapTo',
      readyForReview: 'tapToSubmit',
      // Taking a report back reads by what it takes back.
      planned: status === 'complete' ? 'tapToClear' : 'tapToWithdraw',
    };
    // Only `tapTo` names a status — the one it sets, Done.
    return t(`scheduler.panel.locations.${key[action]}`, { status: t(LOCATION_STATUS_LABEL_KEY.complete) });
  };

  /** The cell's a11y label: where it is, what it reads, the hours worked there
   *  when the IoT has counted any, and what a tap does — the last clause
   *  absent when there is nothing for this user to do there. */
  const cellLabel = (
    status: LocationStatus,
    action: AreaAction | null,
    floorName: string,
    zoneName: string,
    hours: number | undefined,
  ): string => {
    const where = t('scheduler.panel.locations.cellLabel', { floor: floorName, zone: zoneName });
    const worked = hours === undefined ? '' : `, ${t('scheduler.panel.locations.workedHours', { h: hours })}`;
    const reads = `${t(LOCATION_STATUS_LABEL_KEY[status])}${worked}`;
    return action ? `${where}: ${reads} — ${tapPhrase(status, action)}` : `${where}: ${reads}`;
  };

  if (!task) return null;

  // The slider/headline always show the STORED progress — the real value, even
  // on re-open. Area completion keeps it in sync (writes area % while not
  // manual); the slider writes a manual value that sticks.
  const effective = task.progressPercent;

  // What an area SHOWS right now: mid-sweep, every cell in the swept range
  // previews the sweep's status; everything else reflects committed state.
  const previewStatus = (zoneId: string, actual: LocationStatus): LocationStatus => sweep.sweptMeta(zoneId) ?? actual;

  // Live meter counts — computed over the PREVIEW, so the segments slide while
  // a stroke is being spread. Only statuses with areas in them render (a meter
  // has no zero-width segment), so a clean task shows two segments and a late
  // one grows a third — the late colour on every task would be noise, on a
  // late task it is the point.
  const segments = meterSegments(locations.map((location) => previewStatus(location.locationId, location.status)));
  const stateKind = progressState(total, isManual);

  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <h3 className={styles.title}>{t('scheduler.panel.progress.title')}</h3>
        <span className={styles.bigPercent}>{effective}%</span>
      </header>

      {/* Without `tasks.execute` the % above is the whole read: no slider. */}
      {canExecute && (
        <Slider
          value={effective}
          onValueChange={handleSliderChange}
          ariaLabel={t('scheduler.panel.progress.sliderLabel')}
        />
      )}

      {/* State line: no-areas → a plain note; override → manual note + reset;
          otherwise → subtle "auto from areas" cue. */}
      <p className={stateKind === 'auto' ? styles.autoHint : styles.stateLine}>
        {t(PROGRESS_STATE_LABEL_KEY[stateKind])}
        {stateKind === 'manual' && canExecute && (
          <>
            {' '}
            <button type="button" className={styles.assignLink} onClick={handleReset}>
              {t('scheduler.panel.progress.reset')}
            </button>
          </>
        )}
      </p>

      {total > 0 && (
        <div className={styles.areaBlock}>
          {/* Passive meter with live counts — not a tool, just the key. Each
              segment grows with its count (flex-grow = count), so the bar IS
              the distribution; a narrow segment keeps its mark and count and
              lets the label truncate. */}
          <div className={styles.meter} role="list" aria-label={t('scheduler.panel.locations.legend')}>
            {segments.map(({ status, count }) => {
              const label = `${t(LOCATION_STATUS_LABEL_KEY[status])} — ${count}`;
              // A slim segment truncates its word — the marker tooltip says it whole.
              return (
                <Tooltip key={status} variant="marker" content={label}>
                  <span
                    className={`${styles.segment} ${styles[AREA_TONE[status]]}`}
                    style={{ flexGrow: count }}
                    role="listitem"
                    aria-label={label}
                  >
                    <StatusMark status={status} size={11} />
                    <span className={styles.segmentCount} aria-hidden>{count}</span>
                    <span className={styles.segmentLabel} aria-hidden>{t(LOCATION_STATUS_LABEL_KEY[status])}</span>
                  </span>
                </Tooltip>
              );
            })}
          </div>

          <div className={styles.microRow}>
            <p className={styles.microHint}>
              {t(canExecute ? 'scheduler.panel.locations.paintHint' : 'scheduler.panel.locations.paintHintContribute')}
            </p>
          </div>

          <FloorZoneGrid
            rows={boardFloors}
            sweep={sweep}
            classes={BOARD_CLASSES}
            rowStyle={(i) => ({ animationDelay: `${i * 45}ms` })}
            floor={(floor) => {
              // Floor handle = one big cell: it reports the WHOLE floor done —
              // or, on a floor already done, takes that back. Decided on
              // COMMITTED state (not the preview) so a passing stroke can't
              // flip the intent.
              const floorTarget = floorReportTarget(floor.zones.map((zone) => zone.status));
              const floorDone = floor.zones.filter((zone) => previewStatus(zone.id, zone.status) === 'complete').length;
              return {
                // A whole floor at once is a manager's; for anyone else it is a label.
                onClick: canExecute ? () => applyStatuses(floor.zones.map((zone) => zone.id), floorTarget) : undefined,
                count: `${floorDone}/${floor.zones.length}`,
                ariaLabel: t('scheduler.panel.locations.setFloor', { floor: floor.name, status: t(LOCATION_STATUS_LABEL_KEY[floorTarget]) }),
              };
            }}
            cell={(zone, floor) => {
              const shown = previewStatus(zone.id, zone.status);
              const hours = workHoursByZone.get(zone.id);
              // An area with nothing for this user to do (unplanned — the task
              // is not committed; done, for a Limited user; any, for a reader)
              // is a plain cell. Only a manager's write anchors a sweep: a
              // review opens one area, and a Limited user reports one at a time.
              const action = nextAreaAction(zone.status, areaTier);
              return {
                className: `${styles.cell} ${styles[AREA_TONE[shown]]} ${action === null ? '' : TAP_CLASS[action]} ${shown === zone.status ? '' : styles.wet}`,
                ariaLabel: cellLabel(zone.status, action, floor.name, zone.name, hours),
                pressMeta: canExecute && action !== 'review' ? action : null,
                onTap: action === null ? undefined : () => tap(zone.id, action),
                content: (
                  <>
                    <span className={styles.cellName}>{zone.name}</span>
                    {/* Trusstor's tag hours in this area — the IoT's count, beside the name like Trusstor's own board. */}
                    {hours !== undefined && (
                      <span className={styles.cellHours} aria-hidden>{t('scheduler.panel.locations.workHours', { h: hours })}</span>
                    )}
                    <span className={styles.cellMark} aria-hidden><StatusMark status={shown} /></span>
                  </>
                ),
              };
            }}
          />
        </div>
      )}
      {review.dialog}
    </section>
  );
}
